// Pieteikšanās ekrāns: ieeja ar e-pastu un paroli, reģistrācija, paroles atjaunošana (saite pa e-pastu),
// neobligāti Google, izstrādes ieeja testiem.

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import type { Me } from '@balansa/shared';
import { api } from '../lib/api';
import { IconKey } from '../lib/icons';
import { pendingNext } from '../lib/next';
import { useApp } from '../store';

type Mode = 'login' | 'register' | 'forgot' | 'reset';

export function Login() {
  const auth = useApp((s) => s.auth);
  const err = useApp((s) => s.loginError);
  const resetToken = useApp((s) => s.resetToken);
  const [mode, setMode] = useState<Mode>(resetToken ? 'reset' : 'login');
  const [email, setEmail] = useState('');

  const go = (m: Mode) => {
    useApp.setState({ loginError: null });
    if (m !== 'reset') useApp.getState().clearResetToken();
    setMode(m);
  };

  return (
    <div className="login">
      <div className="login-card card">
        <LoginArt />
        <h1>Balansa aplis</h1>
        <p className="login-sub">Dzīves jomu līdzsvars, enerģijas baterija un aktivitāšu kārtis — vienuviet.</p>

        {mode === 'login' || mode === 'register' ? (
          <div className="auth-tabs" role="tablist" aria-label="Pieteikšanās veids">
            <button type="button" role="tab" aria-selected={mode === 'login'} onClick={() => go('login')} data-testid="tab-login">
              Ieiet
            </button>
            {auth?.registration ? (
              <button type="button" role="tab" aria-selected={mode === 'register'} onClick={() => go('register')} data-testid="tab-register">
                Izveidot kontu
              </button>
            ) : null}
          </div>
        ) : null}

        {pendingNext() && mode !== 'reset' ? (
          <p className="login-oauth" data-testid="login-oauth">
            Pieslēdzies, lai atļautu AI aģentam (piem. ChatGPT) piekļuvi tavam aplim — pēc tam atvērsies piekrišanas lapa.
          </p>
        ) : null}

        {err ? (
          <p className="login-err" role="alert">
            {err}
          </p>
        ) : null}

        {mode === 'login' ? <LoginForm email={email} setEmail={setEmail} onForgot={() => go('forgot')} /> : null}
        {mode === 'register' ? <RegisterForm email={email} setEmail={setEmail} /> : null}
        {mode === 'forgot' ? <ForgotForm email={email} setEmail={setEmail} onBack={() => go('login')} /> : null}
        {mode === 'reset' && resetToken ? <ResetForm token={resetToken} onRestart={() => go('forgot')} onBack={() => go('login')} /> : null}

        {(mode === 'login' || mode === 'register') && auth?.google ? (
          <>
            <div className="auth-or">
              <span>vai</span>
            </div>
            <a className="btn login-google" href="/api/auth/google" data-testid="login-google">
              <IconKey />
              Turpināt ar Google kontu
            </a>
          </>
        ) : null}

        {mode === 'register' ? (
          <p className="login-note">Jaunam kontam tiek izveidots noklusējuma aplis: Veselība, Draugi, Ģimene, Darbs, Hobiji, Miegs, Uzturs.</p>
        ) : null}

        {auth?.devLogin && (mode === 'login' || mode === 'register') ? <DevLogin /> : null}
      </div>
    </div>
  );
}

function LoginArt() {
  return (
    <svg className="login-art" viewBox="-110 -110 220 220" aria-hidden="true">
      {[20, 40, 60, 80, 100].map((r) => (
        <circle key={r} r={r} className="la-ring" />
      ))}
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <line key={a} x1="0" y1="0" x2={100 * Math.cos(((a - 90) * Math.PI) / 180)} y2={100 * Math.sin(((a - 90) * Math.PI) / 180)} className="la-ring" />
      ))}
      <polygon className="la-poly" points="0,-80 52,-30 69,40 0,60 -43,25 -78,-45" />
      {[[0, -80, '#3f8f6b'], [52, -30, '#d08a2e'], [69, 40, '#2f7f8f'], [0, 60, '#c4566b'], [-43, 25, '#7a5bb5'], [-78, -45, '#4a62b3']].map(([x, y, c]) => (
        <circle key={String(c)} cx={x} cy={y} r="8" fill={String(c)} className="la-dot" />
      ))}
    </svg>
  );
}

/** Paroles lauks ar “Rādīt/Slēpt”. */
export function PasswordInput(p: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'current-password' | 'new-password';
  minLength?: number;
  testId?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="field">
      <label htmlFor={p.id}>{p.label}</label>
      <div className="pw-wrap">
        <input
          id={p.id}
          className="inp"
          type={show ? 'text' : 'password'}
          required
          minLength={p.minLength}
          maxLength={200}
          autoComplete={p.autoComplete}
          value={p.value}
          onChange={(e) => p.onChange(e.target.value)}
          data-testid={p.testId}
        />
        <button type="button" className="pw-eye" onClick={() => setShow(!show)} aria-label={show ? 'Slēpt paroli' : 'Rādīt paroli'} aria-pressed={show}>
          {show ? 'Slēpt' : 'Rādīt'}
        </button>
      </div>
    </div>
  );
}

function FormShell(p: { onSubmit: () => Promise<string | null | void>; children: ReactNode; testId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    const r = await p.onSubmit();
    if (typeof r === 'string') setError(r);
    setBusy(false);
  };
  return (
    <form className="auth-form" onSubmit={submit} data-testid={p.testId} aria-busy={busy}>
      {p.children}
      {error ? (
        <p className="login-err" role="alert" data-testid="auth-error">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function LoginForm(p: { email: string; setEmail: (v: string) => void; onForgot: () => void }) {
  const loginPassword = useApp((s) => s.loginPassword);
  const [password, setPassword] = useState('');
  return (
    <FormShell testId="login-form" onSubmit={() => loginPassword(p.email.trim(), password)}>
      <div className="field">
        <label htmlFor="li-email">E-pasts</label>
        <input id="li-email" className="inp" type="email" required autoComplete="email" maxLength={200} value={p.email} onChange={(e) => p.setEmail(e.target.value)} />
      </div>
      <PasswordInput id="li-pw" label="Parole" value={password} onChange={setPassword} autoComplete="current-password" />
      <div className="auth-row">
        <button type="submit" className="btn primary" data-testid="login-submit">
          Ieiet
        </button>
        <button type="button" className="linkish" onClick={p.onForgot} data-testid="forgot-link">
          Aizmirsi paroli?
        </button>
      </div>
    </FormShell>
  );
}

function RegisterForm(p: { email: string; setEmail: (v: string) => void }) {
  const register = useApp((s) => s.register);
  const min = useApp((s) => s.auth?.minPasswordLength ?? 8);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  return (
    <FormShell
      testId="register-form"
      onSubmit={async () => {
        if (password !== repeat) return 'Paroles nesakrīt.';
        return register(name.trim(), p.email.trim(), password);
      }}
    >
      <div className="field">
        <label htmlFor="rg-name">Vārds</label>
        <input id="rg-name" className="inp" autoComplete="name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="kā tevi uzrunāt" />
      </div>
      <div className="field">
        <label htmlFor="rg-email">E-pasts</label>
        <input id="rg-email" className="inp" type="email" required autoComplete="email" maxLength={200} value={p.email} onChange={(e) => p.setEmail(e.target.value)} />
      </div>
      <PasswordInput id="rg-pw" label={`Parole (vismaz ${min} simboli)`} value={password} onChange={setPassword} autoComplete="new-password" minLength={min} />
      <PasswordInput id="rg-pw2" label="Parole vēlreiz" value={repeat} onChange={setRepeat} autoComplete="new-password" minLength={min} />
      <div className="auth-row">
        <button type="submit" className="btn primary" data-testid="register-submit">
          Izveidot kontu
        </button>
      </div>
    </FormShell>
  );
}

function ForgotForm(p: { email: string; setEmail: (v: string) => void; onBack: () => void }) {
  const auth = useApp((s) => s.auth);
  const [sent, setSent] = useState(false);
  if (auth?.passwordReset === 'admin')
    return (
      <div className="auth-form">
        <h2 className="auth-h">Paroles atjaunošana</h2>
        <p className="auth-info" data-testid="forgot-admin">
          Šajā serverī e-pasta sūtīšana nav ieslēgta, tāpēc saiti nevaram nosūtīt automātiski. Sazinies ar administratoru — viņš var
          izsniegt vienreizēju paroles atjaunošanas saiti.
        </p>
        <div className="auth-row">
          <button type="button" className="btn" onClick={p.onBack}>
            ← Atpakaļ uz ieeju
          </button>
        </div>
      </div>
    );
  if (sent)
    return (
      <div className="auth-form">
        <h2 className="auth-h">Pārbaudi e-pastu</h2>
        <p className="auth-info" data-testid="forgot-sent">
          Ja konts ar adresi <b>{p.email.trim()}</b> eksistē, uz to nosūtījām saiti jaunas paroles iestatīšanai. Saite derīga 60 minūtes —
          ja vēstule nepienāk, pārbaudi arī mēstuļu mapi.
        </p>
        <div className="auth-row">
          <button type="button" className="btn" onClick={p.onBack}>
            ← Atpakaļ uz ieeju
          </button>
        </div>
      </div>
    );
  return (
    <FormShell
      testId="forgot-form"
      onSubmit={async () => {
        try {
          await api.post('/auth/forgot', { email: p.email.trim() });
          setSent(true);
          return null;
        } catch (e) {
          return (e as Error).message;
        }
      }}
    >
      <h2 className="auth-h">Paroles atjaunošana</h2>
      <p className="auth-info">Ievadi konta e-pastu — nosūtīsim saiti jaunas paroles iestatīšanai.</p>
      <div className="field">
        <label htmlFor="fg-email">E-pasts</label>
        <input id="fg-email" className="inp" type="email" required autoComplete="email" maxLength={200} value={p.email} onChange={(e) => p.setEmail(e.target.value)} />
      </div>
      <div className="auth-row">
        <button type="submit" className="btn primary" data-testid="forgot-submit">
          Sūtīt saiti
        </button>
        <button type="button" className="linkish" onClick={p.onBack}>
          Atpakaļ uz ieeju
        </button>
      </div>
    </FormShell>
  );
}

function ResetForm(p: { token: string; onRestart: () => void; onBack: () => void }) {
  const enter = useApp((s) => s.enter);
  const min = useApp((s) => s.auth?.minPasswordLength ?? 8);
  const [check, setCheck] = useState<{ ok: boolean; email?: string; message?: string } | null>(null);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');

  useEffect(() => {
    api
      .post<{ email: string }>('/auth/reset/check', { token: p.token })
      .then((r) => setCheck({ ok: true, email: r.email }))
      .catch((e: Error) => setCheck({ ok: false, message: e.message }));
  }, [p.token]);

  if (!check) return <p className="auth-info">Pārbauda saiti…</p>;
  if (!check.ok)
    return (
      <div className="auth-form">
        <h2 className="auth-h">Saite nav derīga</h2>
        <p className="login-err" role="alert" data-testid="reset-invalid">
          {check.message}
        </p>
        <div className="auth-row">
          <button type="button" className="btn primary" onClick={p.onRestart}>
            Pieprasīt jaunu saiti
          </button>
          <button type="button" className="linkish" onClick={p.onBack}>
            Atpakaļ uz ieeju
          </button>
        </div>
      </div>
    );
  return (
    <FormShell
      testId="reset-form"
      onSubmit={async () => {
        if (password !== repeat) return 'Paroles nesakrīt.';
        try {
          const me = await api.post<Me>('/auth/reset', { token: p.token, password });
          await enter(me);
          useApp.getState().showToast('Jaunā parole iestatīta — esi pieteicies ✓');
          return null;
        } catch (e) {
          return (e as Error).message;
        }
      }}
    >
      <h2 className="auth-h">Jauna parole</h2>
      <p className="auth-info">
        Kontam <b>{check.email}</b>. Pēc nomaiņas visās citās ierīcēs no konta tiks izrakstīts.
      </p>
      <PasswordInput id="rs-pw" label={`Jaunā parole (vismaz ${min} simboli)`} value={password} onChange={setPassword} autoComplete="new-password" minLength={min} />
      <PasswordInput id="rs-pw2" label="Jaunā parole vēlreiz" value={repeat} onChange={setRepeat} autoComplete="new-password" minLength={min} />
      <div className="auth-row">
        <button type="submit" className="btn primary" data-testid="reset-submit">
          Saglabāt un ieiet
        </button>
      </div>
    </FormShell>
  );
}

function DevLogin() {
  const devLogin = useApp((s) => s.devLogin);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="login-dev"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        await devLogin(email.trim(), name.trim());
        setBusy(false);
      }}
    >
      <div className="login-dev-h">
        Izstrādes pieteikšanās <span>(tikai testiem — production vidē izslēgta)</span>
      </div>
      <input className="inp" type="email" required placeholder="e-pasts@piemers.lv" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Izstrādes e-pasts" />
      <input className="inp" placeholder="Vārds (neobligāti)" value={name} onChange={(e) => setName(e.target.value)} aria-label="Izstrādes vārds" />
      <button className="btn" type="submit" disabled={busy} data-testid="dev-login">
        Ieiet bez paroles
      </button>
    </form>
  );
}
